/**
 * Billing account agreement slice pieces — the pricing/term agreements that
 * hang off a billing account, active ones sorted first.
 *
 * Contains:
 * - accountAgreementsInitialState — adapter state (no view substate)
 * - addAccountAgreementCases — load-list / create / edit extra reducers
 * - agreement selectors, including the per-account memoized selectors that
 *   sort active agreements ahead of inactive ones
 */
import { createSelector } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';

import {
  createBillingAccountAgreement,
  editBillingAccountAgreement,
  loadBillingAccountAgreements,
} from '@sophocles/features/billing/api';
import { BillingAccountAgreementSchema } from '@sophocles/features/billing/schema';
import { billingAccountAgreementsAdapter } from '@sophocles/features/billing/slices/adapters';
import { selectBillingAccount } from '@sophocles/features/billing/slices/billingAccounts';
import { memoizeByKey } from '@sophocles/utils/memoizeByKey';

export const accountAgreementsInitialState = billingAccountAgreementsAdapter.getInitialState();

export const addAccountAgreementCases = builder => {
  builder
    .addCase(loadBillingAccountAgreements.fulfilled, (state, action) => {
      const normalized = normalize(action.payload, [BillingAccountAgreementSchema]);
      // normalize omits the key entirely for an empty result set, and
      // upsertMany throws on undefined.
      billingAccountAgreementsAdapter.upsertMany(state.accountAgreements, normalized.entities.billingAgreements ?? {});
    })
    .addCase(createBillingAccountAgreement.fulfilled, (state, action) => {
      billingAccountAgreementsAdapter.upsertOne(state.accountAgreements, action.payload);
    })
    .addCase(editBillingAccountAgreement.fulfilled, (state, action) => {
      billingAccountAgreementsAdapter.upsertOne(state.accountAgreements, action.payload);
    });
};

export const selectBillingAccountAgreementEntities = state => {
  return state.billing.accountAgreements;
};
export const { selectById: selectBillingAccountAgreementById, selectIds: selectBillingAccountAgreementIds } =
  billingAccountAgreementsAdapter.getSelectors(selectBillingAccountAgreementEntities);
export const selectBillingAccountAgreement = id => state => selectBillingAccountAgreementById(state, id);

export const selectBillingAccountAgreements = memoizeByKey(accountId =>
  createSelector(
    selectBillingAccountAgreementEntities,
    state => state,
    (agreements, state) => {
      if (agreements && agreements.ids) {
        const sortedAgreements = agreements.ids
          .map(agreementId => selectBillingAccountAgreementById(state, agreementId))
          .filter(agreement => agreement != null && agreement.account === accountId);
        sortedAgreements.sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));
        return sortedAgreements;
      }
    },
  ),
);

export const selectBillingAgreementsForAccount = memoizeByKey(accountId =>
  createSelector(
    selectBillingAccount(accountId),
    state => state,
    (billingAccount, state) => {
      if (billingAccount && billingAccount.agreements) {
        const agreements = billingAccount.agreements
          .map(agreementId => selectBillingAccountAgreementById(state, agreementId))
          .filter(agreement => agreement != null);

        agreements.sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));

        return agreements;
      }
      return [];
    },
  ),
);
